import React, { useState, useMemo } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import {
  usePerformSparkCalendarTasks,
  type PerformSparkCalendarTask,
} from '../../hooks/usePerformSparkCalendarTasks';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TASK_TYPE_COLORS: Record<string, string> = {
  Review: '#8B5CF6',
  Goal: '#10B981',
  Calibration: '#3B82F6',
  Survey: '#F59E0B',
  Approval: '#EF4444',
  Delivery: '#F97316',
  Acknowledgement: '#F97316',
};

export const PerformSparkCalendarWidget: React.FC = () => {
  const today = new Date();
  const navigate = useNavigate();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const { tasks, loading, error } = usePerformSparkCalendarTasks(viewYear, viewMonth);

  const tasksByDay = useMemo(() => {
    const map: Record<number, PerformSparkCalendarTask[]> = {};
    tasks.forEach((task) => {
      const day = parseInt(task.taskDate.split('-')[2], 10);
      if (!map[day]) map[day] = [];
      map[day].push(task);
    });
    return map;
  }, [tasks]);

  const typesByDay = useMemo(() => {
    const map: Record<number, string[]> = {};
    Object.entries(tasksByDay).forEach(([day, dayTasks]) => {
      const types: string[] = [];
      dayTasks.forEach((t) => {
        if (!types.includes(t.taskType)) types.push(t.taskType);
      });
      map[Number(day)] = types;
    });
    return map;
  }, [tasksByDay]);

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const monthLabel = new Date(viewYear, viewMonth).toLocaleString('default', { month: 'long' });

  const isToday = (day: number) =>
    viewYear === today.getFullYear() && viewMonth === today.getMonth() && day === today.getDate();

  const goToPrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const weeks = useMemo(() => {
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const result: (number | null)[][] = [];
    for (let r = 0; r < cells.length; r += 7) {
      result.push(cells.slice(r, r + 7));
    }
    return result;
  }, [firstDayOfMonth, daysInMonth]);

  return (
    <div className="flex flex-col h-full p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h3 className="text-base font-semibold text-gray-800">Calendar</h3>
        <div className="flex items-center gap-1.5">
          <button onClick={goToPrevMonth} className="p-1 hover:bg-gray-100 rounded-md transition-colors">
            <CaretLeft size={16} className="text-gray-500" />
          </button>
          <span className="text-sm font-medium text-gray-700 min-w-[110px] text-center">
            {monthLabel} {viewYear}
          </span>
          <button onClick={goToNextMonth} className="p-1 hover:bg-gray-100 rounded-md transition-colors">
            <CaretRight size={16} className="text-gray-500" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-gray-200 rounded-full animate-spin" style={{ borderTopColor: '#4B45E5' }} />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">{error}</div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 border border-gray-200 rounded-lg overflow-visible">
          {/* Day headers */}
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200 flex-shrink-0">
            {DAYS.map((d, i) => (
              <div key={i} className={`text-center text-[11px] font-semibold text-gray-500 py-1.5 ${i < 6 ? 'border-r border-gray-200' : ''}`}>
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="flex-1 grid auto-rows-fr" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {weeks.flat().map((day, idx) => {
              const ci = idx % 7;
              const ri = Math.floor(idx / 7);
              const isLastRow = ri === weeks.length - 1;
              const isLastCol = ci === 6;

              if (day === null) {
                return (
                  <div
                    key={idx}
                    className={`bg-gray-50/50 ${!isLastCol ? 'border-r border-gray-200' : ''} ${!isLastRow ? 'border-b border-gray-200' : ''}`}
                  />
                );
              }

              const dayTasks = tasksByDay[day];
              const eventTypes = typesByDay[day];
              const hasEvents = !!eventTypes;
              const isTodayCell = isToday(day);
              const isSunday = ci === 0;
              const isHovered = hoveredDay === day && hasEvents;

              return (
                <div
                  key={idx}
                  className={`
                    relative flex items-center justify-center cursor-default
                    ${!isLastCol ? 'border-r border-gray-200' : ''}
                    ${!isLastRow ? 'border-b border-gray-200' : ''}
                    ${isTodayCell ? 'bg-indigo-50' : hasEvents ? 'bg-white hover:bg-gray-50' : 'bg-white'}
                  `}
                  onMouseEnter={() => hasEvents && setHoveredDay(day)}
                  onMouseLeave={() => setHoveredDay(null)}
                >
                  <span
                    className={`
                      inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium
                      ${isTodayCell ? 'bg-indigo-600 text-white' : isSunday ? 'text-gray-400' : 'text-gray-700'}
                    `}
                  >
                    {day}
                  </span>

                  {/* Event count badge */}
                  {hasEvents && dayTasks && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[9px] font-semibold text-indigo-600 bg-indigo-50 rounded px-1 leading-[14px]">
                      {dayTasks.length}
                    </span>
                  )}

                  {/* Tooltip */}
                  {isHovered && dayTasks && (
                    <div
                      className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-64"
                      style={{
                        ...(ri <= 1
                          ? { top: '100%', marginTop: '2px' }
                          : { bottom: '100%', marginBottom: '2px' }
                        ),
                        left: ci <= 1 ? '0' : ci >= 5 ? 'auto' : '50%',
                        right: ci >= 5 ? '0' : 'auto',
                        transform: ci > 1 && ci < 5 ? 'translateX(-50%)' : 'none',
                      }}
                    >
                      <div className="text-xs font-semibold text-gray-500 mb-2">
                        {new Date(viewYear, viewMonth, day).toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </div>
                      <div className="space-y-2 max-h-36 overflow-auto">
                        {dayTasks.slice(0, 5).map((task, tIdx) => (
                          <div
                            key={tIdx}
                            className={`flex items-start gap-2 ${task.actionUrl ? 'cursor-pointer hover:bg-gray-50 -mx-1 px-1 rounded' : ''}`}
                            onClick={() => task.actionUrl && navigate(task.actionUrl)}
                          >
                            <div
                              className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                              style={{ backgroundColor: TASK_TYPE_COLORS[task.taskType] || '#6B7280' }}
                            />
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">{task.taskName}</p>
                              <p className="text-[11px] text-gray-500">
                                {task.phaseLabel}
                                <span
                                  className="ml-1 text-[10px] font-medium px-1 py-0.5 rounded"
                                  style={{
                                    backgroundColor: `${TASK_TYPE_COLORS[task.taskType] || '#6B7280'}15`,
                                    color: TASK_TYPE_COLORS[task.taskType] || '#6B7280',
                                  }}
                                >
                                  {task.taskType}
                                </span>
                              </p>
                              {task.employeeName && (
                                <p className="text-[10px] text-gray-400 truncate">{task.employeeName}</p>
                              )}
                            </div>
                          </div>
                        ))}
                        {dayTasks.length > 5 && (
                          <p className="text-[11px] text-gray-400">+{dayTasks.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
